import { assertNever } from './assert-never.js';
import { ANIMAL_SOUNDS, CAST_REGISTRY, isAnimal } from './cast.js';
import { DASHES, escapeRegExp, wordsIn } from './coach-voice.js';
import { sceneAsserts, sceneKeyColumn, sceneRole, teachingCount, } from './content.js';
import { languageIssues } from './language.js';
import { CENT } from './money.js';
/**
 * What a row must be before anyone can see it.
 *
 * Pure functions over plain values, for the reason §15 gives about `StreakRules`: these
 * are the rules nobody writes the edge cases for if they need a database to exercise.
 *
 * Every message names the observed value. `api-conventions.md` §9 makes that
 * load-bearing rather than polite — a generator retries `received 0`, and gives up on
 * `Validation failed`.
 */
/**
 * 005 §1 sells five minutes; a lesson outside this is not the product. The writer, its
 * prompts and the reviewer read `SCENES_MAX`; the gate, the API write and the console's
 * buttons read `SCENES_CEILING` below.
 */
export const SCENES_MIN = 5;
/** Owner decision 4, SKILL-11: five to eight scenes fit five minutes, sixteen did not. */
export const SCENES_MAX = 8;
/**
 * What the gate and the console still allow. The built-in first challenge is ten scenes,
 * and GEN-117 raised the gate from eight because it could not be republished; holding
 * the gate to `SCENES_MAX` waits on the owner: `docs/decisions/deferred.md`, SKILL-11.
 */
export const SCENES_CEILING = 16;
/**
 * How long a CASE may be, in characters — GEN-51.
 *
 * Forty seconds of reading: one scene's share of the five minutes `005` §1 sells, at the
 * 180 words a minute a phone reader manages. Why that number, why CASE alone, and why the
 * gate rather than a Zod bound: `docs/decisions/case-length-budget.md`.
 */
export const CASE_BODY_MAX = 700;
/**
 * The closing scene's two halves — GEN-91, owner decision 5. The takeaway is the RECAP's
 * one line, at most `TAKEAWAY_WORDS_MAX` words. Its body says what the rule means, and the floor only refuses
 * a headline with nothing under it: GEN-91's 120 characters was two sentences, which a
 * short takeaway's body no longer needs.
 */
export const TAKEAWAY_WORDS_MAX = 14;
export const RECAP_BODY_MIN = 40;
/**
 * One idea a paragraph, in words: owner decision 3, SKILL-11. The writer is held to it;
 * the publish gate is not, because every published demo lesson runs past it somewhere
 * and SKILL-05's 300 characters, which the gate held, is retired with the rule it was.
 */
export const PARAGRAPH_WORDS_MAX = 22;
/**
 * How long a BUCKET answer gets, in the drop target it is read in — UI-26.
 *
 * Measured as SKILL-05 measured paragraphs: three lines of the 34-character line a
 * drop target gives, past which an answer stops reading as something you pick
 * up. The demo dataset's 116 answers run to 64, so it refuses nothing written.
 *
 * `BUCKET_MIN_WIDTH` is the width this is three lines of, and the two are not
 * derived from each other — `docs/decisions/bucket-answer-width-and-budget.md`.
 */
export const ITEM_LABEL_MAX = 96;
/**
 * What a subheadline may carry — LESSON-16, the card's spec-review answers.
 *
 * Forty words across at most two paragraphs, and at most five of them bold. A paragraph
 * that is bold throughout is emphasis on nothing, so it is refused whatever its length.
 */
export const SUBHEADLINE_WORDS_MAX = 40;
export const SUBHEADLINE_PARAGRAPHS_MAX = 2;
/** The app bolds only the first two marked phrases, so a third is lost — `writing-level`. */
export const SUBHEADLINE_BOLD_PHRASES_MAX = 2;
/** Fewer than two items or buckets leaves the learner nothing to choose between. */
export const OPTIONS_MIN = 2;
const seedItem = (position, label) => ({ position, label });
/**
 * What a scene added in the console starts as, per kind.
 *
 * Seeded rather than empty so the reviewer edits a scene instead of clearing the errors
 * an empty one raises. Here rather than in the console because the gate has to recognise
 * the seed: QA found an added CONCEPT still reading "The idea, in one line." go live.
 * Every string opens with `TO WRITE`, so the lesson's scene list shows what nobody wrote.
 */
export const SCENE_SEEDS = {
    HOOK: { prompt: 'TO WRITE: the opening question.' },
    CONCEPT: { prompt: 'TO WRITE: the idea, in one line.' },
    CASE: { prompt: 'TO WRITE: what happened, and to whom.' },
    ARTICLE: { prompt: 'TO WRITE: what to read next.' },
    RECAP: {
        prompt: 'TO WRITE: what you now know.',
        body: 'TO WRITE: what the rule means, and what to do differently because of it. The publish gate measures this, so a headline on its own is a scene that cannot ship.',
    },
    SPEAK: { prompt: 'TO WRITE: say it out loud.' },
    CHOICE: {
        prompt: 'TO WRITE: what would you do?',
        items: [
            { ...seedItem(1, 'TO WRITE: the right answer'), isCorrect: true },
            { ...seedItem(2, 'TO WRITE: a tempting wrong one'), isCorrect: false },
        ],
    },
    RANK: {
        prompt: 'TO WRITE: put these in order.',
        items: [
            { ...seedItem(1, 'TO WRITE: first'), correctPosition: 1 },
            { ...seedItem(2, 'TO WRITE: second'), correctPosition: 2 },
        ],
    },
    BUCKET: {
        prompt: 'TO WRITE: sort these.',
        buckets: [
            { position: 1, label: 'TO WRITE: one group' },
            { position: 2, label: 'TO WRITE: the other' },
        ],
        // Named by bucket **position** as a string, which is what the write resolves — the
        // buckets have no ids until the same transaction creates them. GEN-23.
        items: [
            {
                ...seedItem(1, 'TO WRITE: belongs in the first'),
                correctBucketId: '1',
            },
            {
                ...seedItem(2, 'TO WRITE: belongs in the second'),
                correctBucketId: '2',
            },
        ],
    },
    SLIDER: {
        prompt: 'TO WRITE: how much?',
        min: 0,
        max: 100,
        step: 5,
        targetMin: 40,
        targetMax: 60,
    },
    ALLOCATE: {
        prompt: 'TO WRITE: divide the budget.',
        poolTotal: 100,
        items: [
            { ...seedItem(1, 'TO WRITE: one call'), correctAmount: 60 },
            { ...seedItem(2, 'TO WRITE: the other'), correctAmount: 40 },
        ],
    },
};
/**
 * Matched whole, not by the `TO WRITE` prefix: the keyless planner writes `TO WRITE`
 * lessons that the nightly QA check publishes, and a model never writes a seed verbatim.
 */
const SEED_TEXT = new Set(Object.values(SCENE_SEEDS).flatMap((seed) => [
    seed.prompt,
    ...(seed.body === undefined ? [] : [seed.body]),
    ...(seed.items ?? []).map((one) => one.label),
]));
function seedIssueParts(scene) {
    const fields = [
        [['prompt'], scene.prompt],
        [['body'], scene.body],
        ...scene.itemLabels.map((label, index) => [
            ['items', String(index), 'label'],
            label,
        ]),
    ];
    return fields.flatMap(([path, text]) => text !== null && SEED_TEXT.has(text)
        ? [
            manual(path, `This is the text a new scene starts with, so nobody has written it yet, received "${text.slice(0, 60)}".`),
        ]
        : []);
}
/**
 * Whether an item carries the key its mechanic grades against.
 *
 * The mapping is `sceneKeyColumn` rather than a chain here, so a twelfth mechanic gets
 * `undefined` from the record — a compile error at the `Record<SceneKind, …>` — instead
 * of falling through a chain as "keyed" and publishing a scene with no answer key.
 */
export function sceneKeyed(kind, item) {
    const column = sceneKeyColumn[kind];
    if (column === null)
        return true;
    if (column === 'isCorrect')
        return item.isCorrect === true;
    return ((item[column] ?? null) !== null);
}
const issue = (path, message) => ({
    path,
    message,
});
/**
 * The one lesson-level rule a planner can check before anything is written.
 *
 * Exported so the generator asks the gate rather than restating it: the count and the
 * sentence are the same fact, and a plan repaired against a different sentence than the
 * reviewer will read is two rules wearing one name.
 *
 * It takes the scenes rather than a number because only the teaching ones count —
 * `sceneTeaches`, and CURR-12 for why the reward screen is not one of them.
 */
export function sceneCountIssue(scenes, max = SCENES_CEILING) {
    const count = teachingCount(scenes);
    return count < SCENES_MIN || count > max
        ? issue('scenes', `A lesson has ${String(SCENES_MIN)} to ${String(max)} teaching scenes, received ${String(count)}.`)
        : null;
}
/**
 * Why a change from this many teaching scenes to that many is refused, or nothing.
 *
 * **Directional**, which is why it takes both counts — a lesson mid-authoring sits below
 * the floor, and a rule stated over the count alone refuses the scene that would fix it;
 * a lesson already past the cap may still lose scenes. Shared by the API's placement
 * write and the console's controls, so a lesson cannot be built past the gate — GEN-117.
 */
export function sceneBoundsRefusal(before, after) {
    if (after > before && after > SCENES_CEILING)
        return `A lesson has at most ${String(SCENES_CEILING)} teaching scenes. Remove one first.`;
    if (after < before && after < SCENES_MIN)
        return `A lesson has at least ${String(SCENES_MIN)} teaching scenes. Add one first.`;
    return undefined;
}
/**
 * Whether a slider can be answered at all — GEN-49.
 *
 * Applied where an edit lands as well as at publish, so the reviewer who typed the number
 * is the one who hears about it. The publish gate has called it since GEN-128, which is
 * what puts it in front of the planner's repair rounds. Every message names the observed
 * value — `api-conventions.md` §9.
 */
export function sliderTrackIssues(track) {
    const { min, max, step, targetMin, targetMax } = track;
    const issues = [];
    if (min !== null && max !== null && min >= max)
        issues.push(issue('min', `The track runs from min ${String(min)} to max ${String(max)}, which is empty.`));
    if (targetMin !== null && targetMax !== null && targetMin > targetMax)
        issues.push(issue('targetMin', `targetMin ${String(targetMin)} is above targetMax ${String(targetMax)}.`));
    for (const [name, value] of [
        ['targetMin', targetMin],
        ['targetMax', targetMax],
    ]) {
        if (value === null)
            continue;
        if (min !== null && value < min)
            issues.push(issue(name, `${name} ${String(value)} is below the track, which starts at ${String(min)}.`));
        if (max !== null && value > max)
            issues.push(issue(name, `${name} ${String(value)} is above the track, which ends at ${String(max)}.`));
    }
    // A step the band cannot land on is the failure nobody looks for: the slider renders,
    // the range reads sensibly, and no reachable value is ever counted correct.
    if (step !== null && step > 0 && min !== null && targetMin !== null)
        if (!bandIsReachable({ min, step, targetMin, targetMax }))
            issues.push(issue('step', `A step of ${String(step)} from ${String(min)} never lands between ${String(targetMin)} and ${String(targetMax ?? targetMin)}.`));
    // The handle starts at `min` (`V2SceneBody`), so a band holding it is answered before
    // the learner moves anything — GEN-128. Below `min` is refused above as off the track.
    if (min !== null && targetMin === min)
        issues.push(issue('targetMin', `The slider starts at ${String(min)}, inside the band ${String(targetMin)} to ${String(targetMax ?? targetMin)}, so the answer is selected before the learner moves it.`));
    return issues;
}
function bandIsReachable(band) {
    const top = band.targetMax ?? band.targetMin;
    // Rounded, or 0.3 + 25 × 0.01 lands at 0.5499… and an exact 0.55 reads unreachable.
    const first = Math.ceil(Number(((band.targetMin - band.min) / band.step).toFixed(9)));
    return Number((band.min + first * band.step).toFixed(9)) <= top;
}
/** As long as a rule's own sentence ever needs to be, and the bound `planIssueSchema` sets. */
export const RULE_KEY_MAX = 500;
/**
 * The stable half of a rule's message — GEN-88.
 *
 * Every message names its observed value after `, received ` (§9), which is what stops
 * the message being a group key: the em dash rule's is forty characters of the author's
 * prose. Dropping it and eliding the digits leaves the sentence the rule always writes;
 * collapsing a run of them covers a rule that enumerates the scene's own buckets.
 *
 * A heuristic over prose, leaked twice. Why a declared key is the real fix:
 * `docs/decisions/planner-repair-records.md`.
 */
export function ruleKeyOf(message) {
    const said = message.indexOf(', received ');
    return (said === -1 ? message : message.slice(0, said))
        .replace(/\d+/g, '#')
        .replace(/#(?:, #)+/g, '#…')
        .slice(0, RULE_KEY_MAX);
}
const fixable = (path, message) => ({
    path,
    message,
    fixable: true,
});
const manual = (path, message) => ({
    path,
    message,
    fixable: false,
});
/**
 * What an ALLOCATE scene owes before it grades anybody: a range per category, and an
 * example split that adds up to the pool.
 *
 * The two are one rule. Every range is symmetric around the example, so a split that
 * adds up is itself proof the ranges can be satisfied together — which is why there is
 * no feasibility check here. `docs/decisions/allocation-ranges.md`.
 */
function allocationIssues(scene) {
    const issues = [];
    const unranged = scene.allocations.filter((one) => one.tolerance === null).length;
    if (unranged > 0)
        issues.push(manual(['items'], `Every category needs an amountTolerance, the range that makes its split one defensible answer rather than the answer. ${String(unranged)} of ${String(scene.allocations.length)} have none.`));
    // Only once every category carries an amount: `unkeyedItems` already says they are
    // missing, and "the split adds up to 0" buries the sentence that helps.
    const keyed = scene.itemCount > 0 && scene.keyedItemCount === scene.itemCount;
    if (keyed && scene.poolTotal !== null) {
        const total = scene.allocations.reduce((sum, one) => sum + (one.amount ?? 0), 0);
        if (Math.abs(total - scene.poolTotal) > CENT)
            issues.push(manual(['items'], `The example split adds up to ${String(total)}, not the poolTotal of ${String(scene.poolTotal)}.`));
    }
    return issues;
}
function needsTwoItems(scene) {
    if (scene.itemCount >= OPTIONS_MIN)
        return [];
    return [
        manual(['items'], `A ${scene.kind} scene requires at least ${String(OPTIONS_MIN)} items, received ${String(scene.itemCount)}.`),
    ];
}
/**
 * An item with nothing in the column its mechanic grades against.
 *
 * The column is read from `sceneKeyColumn` — the record `sceneKeyed` counted with — so
 * the message cannot name a column the count did not check. A `null` column is a kind
 * with no key at all, where `sceneKeyed` counts every item and this can never fire.
 */
function unkeyedItems(scene) {
    const column = sceneKeyColumn[scene.kind];
    if (column === null || scene.keyedItemCount === scene.itemCount)
        return [];
    return [
        manual(['items'], `Every item needs a ${column}, received ${String(scene.keyedItemCount)} of ${String(scene.itemCount)}.`),
    ];
}
/**
 * The dash, the one part of the writing standard this gate holds — SKILL-05, SKILL-11.
 * A dash is a character, so it is decided rather than requested. The paragraph length it
 * held beside it is the writer's now: `docs/decisions/prose-gate.md`.
 */
function proseIssueParts(field, text) {
    const dash = DASHES.exec(text ?? '');
    if (text === null || dash === null)
        return [];
    return [
        manual([field], `A dash reads as machine-written, use a period or a comma, and write a range as "5 to 8", received "${around(text, dash.index)}".`),
    ];
}
/**
 * American spelling, the one part of the language standard a gate can hold — LANG-01.
 *
 * The rest of `languageIssues` warns, because it needs an editor's judgment, and the
 * console shows it with the fixes. `docs/decisions/language-standard.md`.
 */
function spellingIssueParts(field, text) {
    if (text === null)
        return [];
    return languageIssues(text)
        .filter((one) => one.blocks)
        .map((one) => manual([field], one.message));
}
/** Words that grade the learner, which a shared explanation must not open on — GEN-189. */
const VERDICT_OPENER = /^\s*(good call|great call|right call|nice call|right|correct|incorrect|exactly|not quite|not this one|not this time|nope|wrong|spot on|well done|nice work|nice job|great job|good job|great work|good work|nice|great|nailed it|that's right|that's it|you got it)(?=\s*[.,!:;—–]|\s+-\s|\s*$)/i;
/**
 * The verdict an explanation opens on, or undefined. "Yes." and "No." are left alone: on a
 * yes-or-no stem they are the answer. `docs/decisions/no-sources-in-lesson-text.md`.
 */
export const verdictOpenerOf = (text) => VERDICT_OPENER.exec(text.replaceAll('*', ''))?.[1];
function verdictIssueParts(text) {
    const verdict = verdictOpenerOf(text ?? '');
    return verdict === undefined
        ? []
        : [
            fixable(['explanation'], `The explanation is shown after a right and a wrong answer alike, so it opens on the reason and not a verdict, received "${verdict}".`),
        ];
}
const ATTRIBUTION = /\baccording to\b|\b(?:a\s+)?(?:study|studies|research)\s+(?:by|from)\b/gi;
/** A word in a name: capitalized, an acronym, a domain, or a parenthesized acronym. */
const NAME_WORD = /^(?:\(?[A-Z][\w.&'’-]*\)?)$/;
const NAME_JOINER = new Set(['of', 'and', 'for', 'the', '&']);
/** Capitalized only because a sentence starts there. */
const SENTENCE_OPENER = new Set('The A An Then When So But Now Next If And Here Today Later Still Yet Also After Before First Finally This That She He They It We I You Her His Their Our Your My'.split(' '));
/** Roles, which a case quotes as its own people rather than as a source. */
const ROLE = new Set(['CEO', 'CFO', 'COO', 'CTO', 'CMO', 'VP', 'HR']);
const SAYS = /\b(?:explains|says|defines|calls)\b/g;
/**
 * The name in front of a "says", when it reads as a source: an acronym, a domain, or two
 * capitalized words. One capitalized word is a character speaking — "Betty says".
 */
function sourceNameBefore(text, at) {
    const words = text.slice(0, at).trimEnd().split(/\s+/);
    const name = [];
    for (let word = words.pop(); word !== undefined; word = words.pop()) {
        // A full stop ends the sentence before the name, unless it is an initialism: "U.S.".
        if (/[^A-Z.]\.$/.test(word))
            break;
        if (NAME_WORD.test(word))
            name.unshift(word);
        else if (NAME_JOINER.has(word) && name.length > 0)
            name.unshift(word);
        else
            break;
    }
    while (name.length > 0 && SENTENCE_OPENER.has(name[0] ?? ''))
        name.shift();
    while (name.length > 0 && NAME_JOINER.has(name[0] ?? ''))
        name.shift();
    const capitalized = name.filter((word) => !NAME_JOINER.has(word));
    const named = capitalized.length >= 2 ||
        capitalized.some((word) => /\.(?:org|com|gov|edu)$/.test(word) ||
            (/^\(?[A-Z]{2,}\)?$/.test(word) &&
                !ROLE.has(word.replace(/[()]/g, ''))));
    return named ? name.join(' ') : undefined;
}
/** A source's title, and each acronym in parentheses within one: "SEC" in "…Commission (SEC)". */
const titleNames = (titles) => titles.flatMap((title) => [
    title,
    ...[...title.matchAll(/\(([A-Z][A-Z&.]{1,9})\)/g)].map((one) => one[1] ?? ''),
]);
/**
 * What finds the words in learner text that name a source — GEN-196. Built once per scene,
 * since the titles are the lesson's. A CASE or ARTICLE may name the company its source is
 * filed by, so the title match is off there and the phrasing is not.
 * `docs/decisions/no-sources-in-lesson-text.md`.
 */
export function sourceNameFinder(titles, kind) {
    const named = sceneAsserts[kind]
        ? []
        : titleNames(titles)
            .filter((name) => name.length > 0)
            .map((name) => [
            name,
            new RegExp(`(?<![\\w])${escapeRegExp(name)}(?![\\w])`, /[a-z]/.test(name) ? 'i' : ''),
        ]);
    return (text) => [
        ...new Set([
            ...[...text.matchAll(ATTRIBUTION)].map((one) => one[0]),
            ...[...text.matchAll(SAYS)].flatMap((one) => {
                const name = sourceNameBefore(text, one.index);
                return name === undefined ? [] : [`${name} ${one[0]}`];
            }),
            ...named
                .filter(([, pattern]) => pattern.test(text))
                .map(([name]) => name),
        ]),
    ];
}
function sourceNameIssueParts(scene) {
    const fields = [
        ['prompt', scene.prompt],
        ['body', scene.body],
        ['hint', scene.hint],
        ...scene.itemLabels.map((label) => ['items', label]),
        ['explanation', scene.explanation],
    ];
    const namesIn = sourceNameFinder(scene.sourceTitles ?? [], scene.kind);
    return fields.flatMap(([field, text]) => {
        const names = namesIn(text ?? '');
        return names.length === 0
            ? []
            : [
                fixable([field], `Lesson text states the idea in its own words and leaves the source to the source fields, received "${names.join('", "')}".`),
            ];
    });
}
/** A lesson's or a collection's own words, which a learner reads on its card. */
export function titleIssues(row) {
    return ['title', 'summary'].flatMap((field) => spellingIssueParts(field, row[field] ?? null).map((one) => issue(field, one.message)));
}
const BOLD = /\*\*(.+?)\*\*/g;
/** A paragraph is a non-blank line, as `V2Prose` draws it. */
export const paragraphsOf = (text) => text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
/**
 * The subheadline rules, as sentences — LESSON-16. Kept in two halves because only the
 * length half offers Fix with AI so far (`docs/decisions/fix-with-ai.md`).
 */
export const subheadlineIssues = (text) => [
    ...subheadlineLength(text),
    ...subheadlineBold(text),
];
function subheadlineLength(text) {
    const paragraphs = paragraphsOf(text);
    const issues = [];
    const words = wordsIn(text.replaceAll('**', ''));
    if (words > SUBHEADLINE_WORDS_MAX)
        issues.push(`A subheadline runs to ${String(SUBHEADLINE_WORDS_MAX)} words, received ${String(words)}.`);
    if (paragraphs.length > SUBHEADLINE_PARAGRAPHS_MAX)
        issues.push(`A subheadline has at most ${String(SUBHEADLINE_PARAGRAPHS_MAX)} paragraphs, received ${String(paragraphs.length)}.`);
    return issues;
}
function subheadlineBold(text) {
    const paragraphs = paragraphsOf(text);
    const issues = [];
    const bold = [...text.matchAll(BOLD)].length;
    if (bold > SUBHEADLINE_BOLD_PHRASES_MAX)
        issues.push(`A subheadline bolds at most ${String(SUBHEADLINE_BOLD_PHRASES_MAX)} phrases, received ${String(bold)}.`);
    const allBold = paragraphs.filter((line) => line.includes('**') && wordsIn(line.replace(BOLD, '')) === 0).length;
    if (allBold > 0)
        issues.push(`A whole paragraph cannot be bold, received ${String(allBold)}.`);
    // An odd count is a phrase the learner would see wrapped in asterisks.
    const markers = text.split('**').length - 1;
    if (markers % 2 === 1)
        issues.push(`Every ** that opens bold needs one that closes it, received ${String(markers)}.`);
    return issues;
}
/**
 * Enough of the offending line to find it by eye.
 *
 * The path names a field, and a field holds several sentences, so a message that only
 * said "an em dash" would send the reader hunting through the one place they were already
 * looking. Twenty characters either side is the sentence it is in.
 */
function around(text, at) {
    return text.slice(Math.max(0, at - 20), at + 20);
}
/**
 * What a scene's picture owes — VIS-11.
 *
 * Keyed on the visual's own `kind` and not the scene's, so it sits outside the switch
 * beside `proseIssueParts`: a picture survives a change of mechanic, and a rule filed
 * under one kind would stop being applied the moment the scene became another.
 *
 * Only the arms carrying numbers a reader can check against the words beside them. The
 * rest are drawings, and a drawing is not wrong in a way a gate can see.
 */
function visualIssueParts(scene, cast) {
    const { visual } = scene;
    if (visual === undefined)
        return [];
    return [
        statedTotalIssue(visual),
        branchIssue(visual, scene),
        ...soundIssues(visual),
        ...(cast === undefined ? [] : castIssues(visual, cast)),
    ].filter((one) => one !== undefined);
}
function partiesOf(visual) {
    if (visual.kind === 'dialogue')
        return [visual.left, visual.right];
    if (visual.kind === 'situation')
        return visual.cast;
    return [];
}
function propsOf(visual) {
    if (visual.kind === 'dialogue' || visual.kind === 'situation')
        return visual.props ?? [];
    return [];
}
/** Each seated character against the lesson's cast — `docs/decisions/cast.md`. */
function castIssues(visual, cast) {
    return partiesOf(visual).flatMap(({ name, character }) => {
        if (character === undefined)
            return [];
        const entry = cast.find((one) => one.character === character);
        if (entry === undefined)
            return [
                manual(['visual'], `Every character in the picture is in the lesson's cast, received ${character}.`),
            ];
        return entry.role === name
            ? []
            : [
                manual(['visual'], `A character plays one role in a lesson, and ${character} is cast as ${entry.role}, received ${name}.`),
            ];
    });
}
/** An animal says one of its own sounds, and only an animal says a sound. */
function soundIssues(visual) {
    if (visual.kind !== 'dialogue')
        return [];
    return visual.lines.flatMap(({ by, text, sound }) => {
        const { character } = visual[by];
        if (character !== undefined && isAnimal(character)) {
            const own = ANIMAL_SOUNDS[character];
            return sound !== undefined && own.includes(sound)
                ? []
                : [
                    manual(['visual'], `${character} makes sounds, never words: one of ${own.join(', ')}, received ${sound ?? `"${text ?? ''}"`}.`),
                ];
        }
        return sound === undefined
            ? []
            : [
                manual(['visual'], `Only an animal's line is a sound, received ${sound}.`),
            ];
    });
}
/**
 * An arm whose terms compose into a total, against the total it states.
 *
 * Both number-carrying arms are one shape: an opening term, steps that move it, and a
 * stated result. A waterfall's steps are signed and sum; an equation's each carry an
 * operator. One rule rather than one per arm, because the failure is the same one — a
 * drawing whose arithmetic does not come out, which is convincing in a way a paragraph
 * is not.
 *
 * The waterfall predates VIS-11 and had no rule, which is the gap this closes: VIS-11
 * cited it as the precedent for carrying numbers without inheriting the check.
 */
function statedTotalIssue(visual) {
    if (visual.kind === 'waterfall')
        return totalIssue(visual.steps.reduce((running, step) => running + step.value, visual.start.value), visual.end.value);
    if (visual.kind === 'equation')
        return totalIssue(visual.steps.reduce(applyStep, visual.start.value), visual.result.value);
    return undefined;
}
function totalIssue(reached, stated) {
    // NaN compares false against any tolerance, so without this the one sum that divides
    // by zero is the one that publishes.
    if (Number.isNaN(reached))
        return manual(['visual'], 'A step divides by zero, received 0.');
    return Math.abs(reached - stated) > CENT
        ? manual(['visual'], `The figure works out to ${String(reached)}, not the total it states, received ${String(stated)}.`)
        : undefined;
}
function branchIssue(visual, scene) {
    if (visual.kind !== 'branch' || scene.kind !== 'CHOICE')
        return undefined;
    // The run lights the branch the learner picked by its position among the options, so a
    // drawing with a different number of ways is a picture of a different question.
    return visual.branches.length === scene.itemCount
        ? undefined
        : manual(['visual'], `A branch visual draws one way per option, and this scene offers ${String(scene.itemCount)}, received ${String(visual.branches.length)}.`);
}
/** Left to right, as the figure writes it: a business sum has no precedence to respect. */
function applyStep(running, step) {
    switch (step.op) {
        case 'plus':
            return running + step.value;
        case 'minus':
            return running - step.value;
        case 'times':
            return running * step.value;
        case 'divide':
            return step.value === 0 ? Number.NaN : running / step.value;
        default:
            return assertNever(step.op);
    }
}
/** The same rules, addressed from the lesson down — what a `ProblemDetail` carries. */
export const sceneIssues = (scene, cast) => sceneIssueParts(scene, cast).map((one) => rendered({
    path: ['scenes', scene.position, ...one.path],
    message: one.message,
}));
const rendered = ({ path: [head, position, ...rest], message, }) => issue(typeof position === 'number'
    ? `${String(head)}[${String(position)}].${rest.join('.')}`
    : String(head), message);
/** Named as the console labels the field, so the refusal says what to look for. */
const UNSET = {
    subject: 'Topic is not set.',
    difficulty: 'Level is not set.',
    objectName: 'Card illustration is not set.',
};
export function collectionIssues(row) {
    const issues = [];
    if (row.kind === 'MODULE' && row.parentId === null)
        issues.push(issue('parentId', 'Pick the course this module belongs to.'));
    if (row.subject === null)
        issues.push(issue('subject', UNSET.subject));
    if (row.difficulty === null)
        issues.push(issue('difficulty', UNSET.difficulty));
    if (row.objectName === null)
        issues.push(issue('objectName', UNSET.objectName));
    if (row.lessonCount === 0 && row.kind !== 'COURSE')
        issues.push(issue('lessons', 'It holds no lessons yet, so there is nothing to open.'));
    return [...issues, ...titleIssues(row)];
}
export const lessonIssues = (row) => lessonIssueParts(row).map(rendered);
export function lessonIssueParts(row) {
    const issues = [];
    const lessonLevel = (one) => issues.push({ path: [one.path], message: one.message });
    const inScene = (scene, parts) => issues.push(...parts.map((one) => ({
        path: ['scenes', scene.position, ...one.path],
        message: one.message,
    })));
    if (row.difficulty === null)
        lessonLevel(issue('difficulty', UNSET.difficulty));
    if (row.objectName === null)
        lessonLevel(issue('objectName', UNSET.objectName));
    const count = row.scenes.length;
    const tooFew = sceneCountIssue(row.scenes);
    if (tooFew !== null)
        lessonLevel(tooFew);
    const positions = new Set(row.scenes.map((scene) => scene.position));
    if (positions.size !== count)
        lessonLevel(issue('scenes', `Two scenes share a position, received ${String(positions.size)} distinct of ${String(count)}.`));
    titleIssues(row).forEach(lessonLevel);
    for (const scene of row.scenes) {
        inScene(scene, sceneIssueParts(scene, row.cast));
    }
    // What a lesson asks of the cast contract — CAST-11. Not whether the art exists: an
    // undrawn character or prop publishes and plays as a still or a placeholder. Here
    // rather than in `sceneIssueParts`, which cannot know which props have been added.
    if (row.published !== true)
        for (const scene of row.scenes)
            if (scene.visual !== undefined)
                inScene(scene, visualContractIssues(scene.visual, row.props));
    return issues;
}
export function visualContractIssues(visual, props) {
    return [
        ...partiesOf(visual).flatMap(partyContractIssues),
        ...propsOf(visual)
            .filter((prop) => !props.has(prop))
            .map((prop) => manual(['visual'], `A scene shows only props that have been added, received ${prop}.`)),
    ];
}
function partyContractIssues({ character, pose, facing, reaction, }) {
    if (character === undefined)
        return [];
    const entry = CAST_REGISTRY[character];
    const asked = [
        ['pose', pose, entry.poses],
        ['facing', facing, entry.facings],
        ['reaction', reaction, entry.reactions],
    ];
    return asked.flatMap(([what, wanted, allowed]) => wanted === undefined || allowed.includes(wanted)
        ? []
        : [
            manual(['visual'], `A character shows only the ${what}s its contract lists, received ${character} ${wanted}.`),
        ]);
}
/**
 * One scene's rules, addressed relative to the scene — GEN-33.
 *
 * Relative because there are two ways to name the same field: the reviewer's screen wants
 * the string `scenes[3].items`, and the planner's repair round wants the Zod path
 * `['scenes', 2, 'items']`. Emitting segments lets each caller compose the one it needs;
 * emitting either string would make the other parse it back.
 *
 * The `switch` is exhaustive over `SceneKind`, so a twelfth mechanic is a compile error
 * here rather than a kind that publishes with no rules applied to it.
 */
export function sceneIssueParts(scene, 
/** The lesson's, where the caller holds it. A scene alone cannot say who is cast. */
cast) {
    const issues = [];
    switch (scene.kind) {
        case 'CASE': {
            const length = scene.body?.length ?? 0;
            if (length > CASE_BODY_MAX)
                issues.push(fixable(['body'], `A CASE scene is read in about forty seconds, which is ${String(CASE_BODY_MAX)} characters, received ${String(length)}.`));
            break;
        }
        case 'RECAP': {
            const length = scene.body?.trim().length ?? 0;
            if (length < RECAP_BODY_MIN)
                issues.push(fixable(['body'], `A Key Takeaway explains the rule and what follows from it, which is at least ${String(RECAP_BODY_MIN)} characters, received ${String(length)}.`));
            break;
        }
        case 'HOOK':
        case 'CONCEPT':
        case 'ARTICLE':
        case 'REWARD':
            break;
        case 'CHOICE':
            issues.push(...needsTwoItems(scene));
            if (scene.keyedItemCount === 0)
                issues.push(manual(['items'], 'No item is marked correct, so every answer would score zero.'));
            break;
        case 'RANK':
            issues.push(...needsTwoItems(scene), ...unkeyedItems(scene));
            break;
        case 'BUCKET':
            if (scene.bucketCount < OPTIONS_MIN)
                issues.push(manual(['buckets'], `A BUCKET scene requires at least ${String(OPTIONS_MIN)} buckets, received ${String(scene.bucketCount)}.`));
            for (const [index, label] of scene.itemLabels.entries())
                if (label.length > ITEM_LABEL_MAX)
                    issues.push(fixable(['items', String(index), 'label'], `A BUCKET answer is read in a drop target and runs to ${String(ITEM_LABEL_MAX)} characters, received ${String(label.length)} — "${label}".`));
            issues.push(...unkeyedItems(scene));
            break;
        case 'SLIDER':
            // The track, and not only the band — GEN-78. `to-scene.ts` reads these through
            // `num(value, 0)`, so a slider published without one is served to a learner as a
            // track from 0 to 0: draggable nowhere, and correct at the only value it can hold.
            // Reachable since a reviewer could change a question's mechanic, which leaves every
            // one of these null at once.
            for (const [name, value] of [
                ['min', scene.min],
                ['max', scene.max],
                ['step', scene.step],
            ])
                if (value === null)
                    issues.push(manual([name], `A SLIDER scene needs ${name}, received null — a learner would be given a track with no length.`));
            if (scene.targetMin === null || scene.targetMax === null)
                issues.push(manual(['targetMin'], 'A SLIDER scene needs targetMin and targetMax, received null.'));
            else
                issues.push(...sliderTrackIssues(scene).map((one) => manual([one.path], one.message)));
            break;
        case 'ALLOCATE':
            if (scene.poolTotal === null)
                issues.push(manual(['poolTotal'], 'An ALLOCATE scene needs a poolTotal, received null.'));
            issues.push(...unkeyedItems(scene), ...allocationIssues(scene));
            break;
        case 'SPEAK':
            break;
        default:
            return assertNever(scene.kind);
    }
    // Every kind carries prose, and any kind may carry a picture, so both sit outside the
    // switch rather than in ten arms.
    issues.push(...proseIssueParts('prompt', scene.prompt), ...proseIssueParts('body', scene.body), ...(scene.body === null || scene.bodyPredatesRules === true
        ? []
        : [
            ...subheadlineLength(scene.body).map((message) => fixable(['body'], message)),
            ...subheadlineBold(scene.body).map((message) => manual(['body'], message)),
        ]), ...proseIssueParts('hint', scene.hint ?? null), ...seedIssueParts(scene), ...visualIssueParts(scene, cast), ...spellingIssueParts('prompt', scene.prompt), ...spellingIssueParts('body', scene.body), ...spellingIssueParts('hint', scene.hint ?? null), ...scene.itemLabels.flatMap((label) => spellingIssueParts('items', label)), ...spellingIssueParts('explanation', scene.explanation ?? null), ...verdictIssueParts(scene.explanation), ...sourceNameIssueParts(scene));
    // A scene that claims something happened in the world says where it came from.
    //
    // Two reasons, and the second is not about grounding at all: `to-scene.ts` *throws* on
    // an ARTICLE with no source row, because the served contract requires one. Before this,
    // publishing such a lesson was a runtime 500 in the player; now it is a 422 here.
    //
    // Checked at the gate, so an ungrounded draft can still be written — §10 stays
    // draft-permissive.
    if (sceneAsserts[scene.kind]) {
        if (scene.source === null)
            issues.push(manual(['sourceId'], `A ${scene.kind} scene states something as fact and must name its source, received null.`));
        else if (!scene.source.url && !scene.source.citation)
            issues.push(manual(['sourceId'], 'The linked source has neither a url nor a citation, so nobody can follow it.'));
    }
    // Interactive scenes are graded against a question's explanation, and §14.5's verdict
    // arrives with it. One without a question can be answered but never explained.
    //
    // The question's *status* is deliberately not checked. It used to be, and the rule was
    // unsatisfiable: a generated question is DRAFT, no endpoint published one in bulk, and
    // `publishSubtree` published collections and lessons only — so clearing it meant a PATCH
    // per question, five to a lesson. The first live module reported sixteen of its
    // twenty-three blockers here and no console action could resolve any of them.
    //
    // Publishing the collection now publishes the questions its scenes serve, so the
    // reviewer's click is the approval. See `publishSubtree`.
    if (sceneRole[scene.kind] === 'interactive' && scene.questionId === null) {
        issues.push(manual(['questionId'], 'An interactive scene needs a question to explain its answer, received null.'));
    }
    return issues;
}
//# sourceMappingURL=publish-rules.js.map